import type { EntitySchema } from './types';

// Song moods
const MOODS = [
  { value: 'Romantic', label: 'Romantic' },
  { value: 'Sad', label: 'Sad' },
  { value: 'Happy', label: 'Happy' },
  { value: 'Energetic', label: 'Energetic' },
  { value: 'Melancholic', label: 'Melancholic' },
  { value: 'Devotional', label: 'Devotional' },
  { value: 'Inspirational', label: 'Inspirational' },
  { value: 'Party', label: 'Party' },
  { value: 'Chill', label: 'Chill' },
];

// Song genres/types
const SONG_GENRES = [
  { value: 'Melody', label: 'Melody' },
  { value: 'Item Song', label: 'Item Song' },
  { value: 'Duet', label: 'Duet' },
  { value: 'Solo', label: 'Solo' },
  { value: 'Kuthu', label: 'Kuthu' },
  { value: 'Folk', label: 'Folk' },
  { value: 'Classical', label: 'Classical' },
  { value: 'Rock', label: 'Rock' },
  { value: 'Pop', label: 'Pop' },
  { value: 'Hip-Hop', label: 'Hip-Hop' },
  { value: 'Gaana', label: 'Gaana' },
];

// Languages
const LANGUAGES = [
  { value: 'Tamil', label: 'Tamil' },
  { value: 'Hindi', label: 'Hindi' },
  { value: 'Telugu', label: 'Telugu' },
  { value: 'Malayalam', label: 'Malayalam' },
  { value: 'Kannada', label: 'Kannada' },
  { value: 'English', label: 'English' },
];

export const movieSongSchema: EntitySchema = {
  entityType: 'movie_song',
  displayName: 'Movie Song',
  description: 'Songs from movies with singer, composer, and lyricist information',
  categories: [
    {
      name: 'Basic Information',
      description: 'Release year, duration, and language',
      fields: [
        {
          path: 'basic.releaseYear',
          label: 'Release Year',
          type: 'number',
          operators: ['eq', 'gt', 'gte', 'lt', 'lte', 'between'],
          placeholder: 'e.g., 2020',
          description: 'Year of release',
        },
        {
          path: 'basic.duration',
          label: 'Duration (seconds)',
          type: 'number',
          operators: ['eq', 'gt', 'gte', 'lt', 'lte', 'between'],
          placeholder: 'e.g., 240',
          description: 'Song duration in seconds',
        },
        {
          path: 'basic.language',
          label: 'Language',
          type: 'string',
          operators: ['eq'],
          options: LANGUAGES,
          description: 'Song language',
        },
      ],
    },
    {
      name: 'Movie Information',
      description: 'Associated movie details',
      fields: [
        {
          path: 'movie.title',
          label: 'Movie Title',
          type: 'string',
          operators: ['eq', 'contains'],
          options: 'dynamic',
          description: 'Title of the movie',
        },
        {
          path: 'movie.genre',
          label: 'Movie Genre',
          type: 'array',
          operators: ['contains'],
          options: [
            { value: 'Action', label: 'Action' },
            { value: 'Comedy', label: 'Comedy' },
            { value: 'Drama', label: 'Drama' },
            { value: 'Romance', label: 'Romance' },
            { value: 'Thriller', label: 'Thriller' },
          ],
          description: 'Genre of the movie',
        },
      ],
    },
    {
      name: 'People',
      description: 'Singers, composers, and lyricists',
      fields: [
        {
          path: 'people.singers',
          label: 'Singer',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Song singer(s)',
        },
        {
          path: 'people.composers',
          label: 'Composer',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Music composer(s)',
        },
        {
          path: 'people.lyricists',
          label: 'Lyricist',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Lyric writer(s)',
        },
        {
          path: 'people.directors',
          label: 'Movie Director',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Director of the movie',
        },
        {
          path: 'people.actors',
          label: 'Featured Actor',
          type: 'array',
          operators: ['contains'],
          options: 'dynamic',
          description: 'Actors featured in the song',
        },
      ],
    },
    {
      name: 'Categories',
      description: 'Mood, genre, and occasion',
      fields: [
        {
          path: 'categories.mood',
          label: 'Mood',
          type: 'array',
          operators: ['contains'],
          options: MOODS,
          description: 'Song mood/vibe',
        },
        {
          path: 'categories.genre',
          label: 'Song Genre',
          type: 'array',
          operators: ['contains'],
          options: SONG_GENRES,
          description: 'Type of song',
        },
        {
          path: 'categories.occasion',
          label: 'Occasion',
          type: 'array',
          operators: ['contains'],
          options: [
            { value: 'Wedding', label: 'Wedding' },
            { value: 'Party', label: 'Party' },
            { value: 'Festival', label: 'Festival' },
            { value: 'Birthday', label: 'Birthday' },
          ],
          description: 'Suitable occasions',
        },
      ],
    },
    {
      name: 'Metrics',
      description: 'Popularity and streaming metrics',
      fields: [
        {
          path: 'metrics.views',
          label: 'YouTube Views',
          type: 'number',
          operators: ['eq', 'gt', 'gte', 'lt', 'lte', 'between'],
          placeholder: 'e.g., 1000000',
          description: 'YouTube view count',
        },
        {
          path: 'metrics.popularity',
          label: 'Popularity Score',
          type: 'number',
          operators: ['eq', 'gt', 'gte', 'lt', 'lte', 'between'],
          placeholder: '0-100',
          description: 'Popularity score (0-100)',
        },
      ],
    },
  ],
};
